import { 
  Card, 
  CardContent, 
  Typography, 
  Paper, 
  Box, 
  IconButton, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  Toolbar,
  Chip,
  Avatar,
  LinearProgress,
  useTheme,
  alpha,
  styled,
  Badge,
  Tooltip,
  Fade,
  Zoom,
  Divider,
  Button,
  Grid
} from "@mui/material";
import { 
  Delete, 
  Edit, 
  Kitchen, 
  KeyboardArrowDown, 
  KeyboardArrowUp,
  TrendingUp,
  TrendingDown,
  Event,
  MonetizationOn,
  ArrowLeft,
  ArrowRight,
  Restaurant,
  Receipt,
  AttachMoney,
  CalendarToday,
  FilterList,
  Download,
  Print,
  Refresh,
  CheckCircle,
  Warning,
  Info,
  Visibility,
  MoreVert,
  Add,
  MergeType
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import { format, parseISO, addMonths, subMonths, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { Fragment } from 'react';
import { motion, AnimatePresence } from "framer-motion";

// Styled Components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    cursor: 'pointer',
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    }
  },
  transition: 'background-color 0.2s ease'
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: '#FFFFFF',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  }
}));

// Helper function to merge identical items by product
const mergeItemsByProduct = (items) => {
  const mergedMap = new Map();
  
  items.forEach(item => {
    const key = `${item.productId || item.productName}`;
    
    if (mergedMap.has(key)) {
      const existing = mergedMap.get(key);
      existing.quantity += item.quantity;
      // Keep the original soldPrice and costPrice (assuming they're the same for same product)
      // If prices might differ, you could calculate weighted average
      existing.soldPrice = existing.soldPrice; // Keep original or calculate average
      existing.costPrice = existing.costPrice;
    } else {
      mergedMap.set(key, {
        ...item,
        quantity: item.quantity
      });
    }
  });
  
  return Array.from(mergedMap.values());
};

// Helper function to group servings by date with merged items
const groupServingsByDate = (servings, mergeItems = true) => {
  const grouped = new Map();
  
  servings.forEach(serving => {
    const dateKey = format(parseISO(serving.date), 'yyyy-MM-dd');
    
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, {
        id: `grouped-${dateKey}`,
        date: serving.date,
        items: [],
        originalServings: [],
        dateKey: dateKey
      });
    }
    
    const group = grouped.get(dateKey);
    group.originalServings.push(serving);
    group.items.push(...serving.items);
  });
  
  let groupedArray = Array.from(grouped.values());
  
  // Merge identical items within each grouped date
  if (mergeItems) {
    groupedArray = groupedArray.map(group => ({
      ...group,
      items: mergeItemsByProduct(group.items)
    }));
  }
  
  return groupedArray;
};

export const DailyServingsTable = ({ dailyServings, editDailyServing, deleteDailyServing }) => {
  const theme = useTheme();
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedDate, setSelectedDate] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [groupByDate, setGroupByDate] = useState(true);
  const [mergeSameItems, setMergeSameItems] = useState(true); // New state for merging toggle

  // Group servings by date with optional item merging
  const groupedServings = useMemo(() => {
    if (!groupByDate) {
      // When not grouping by date, still merge items within each individual serving if enabled
      if (mergeSameItems) {
        return dailyServings.map(s => ({ 
          ...s, 
          isGrouped: false, 
          originalServings: [s],
          items: mergeItemsByProduct(s.items)
        }));
      }
      return dailyServings.map(s => ({ ...s, isGrouped: false, originalServings: [s] }));
    }
    return groupServingsByDate(dailyServings, mergeSameItems);
  }, [dailyServings, groupByDate, mergeSameItems]);

  // Calculate statistics from grouped data
  const stats = groupedServings.reduce((acc, serving) => {
    const revenue = serving.items.reduce((sum, item) => sum + (item.quantity * (item.soldPrice || 0)), 0);
    const cost = serving.items.reduce((sum, item) => sum + (item.quantity * (item.costPrice || 0)), 0);
    acc.totalRevenue += revenue;
    acc.totalCost += cost;
    acc.totalProfit += (revenue - cost);
    acc.totalItems += serving.items.reduce((sum, item) => sum + item.quantity, 0);
    return acc;
  }, { totalRevenue: 0, totalCost: 0, totalProfit: 0, totalItems: 0 });

  const avgMargin = stats.totalRevenue > 0 ? (stats.totalProfit / stats.totalRevenue * 100) : 0;

  const handleMonthChange = (direction) => {
    const current = parseISO(`${selectedMonth}-01`);
    const newDate = direction === 'next' ? addMonths(current, 1) : subMonths(current, 1);
    setSelectedMonth(format(newDate, 'yyyy-MM'));
    setSelectedDate(null);
  };

  const handleDateChange = (date) => {
    if (date) {
      setSelectedMonth(format(date, 'yyyy-MM'));
      setSelectedDate(date);
    }
  };

  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredServings = groupedServings.filter(serving => {
    const servingDate = new Date(serving.date);
    const [year, month] = selectedMonth.split('-');
    const inSelectedMonth = servingDate.getFullYear() === parseInt(year) && 
                           servingDate.getMonth() === parseInt(month) - 1;
    
    if (!selectedDate) return inSelectedMonth;
    
    const selected = new Date(selectedDate);
    return inSelectedMonth && isSameDay(servingDate, selected);
  });

  const sortedServings = [...filteredServings].sort((a, b) => {
    if (orderBy === 'date') {
      return order === 'asc' 
        ? new Date(a.date) - new Date(b.date) 
        : new Date(b.date) - new Date(a.date);
    }
    
    if (orderBy === 'revenue') {
      const aRevenue = a.items.reduce((sum, item) => sum + (item.quantity * (item.soldPrice || 0)), 0);
      const bRevenue = b.items.reduce((sum, item) => sum + (item.quantity * (item.soldPrice || 0)), 0);
      return order === 'asc' ? aRevenue - bRevenue : bRevenue - aRevenue;
    }
    
    if (orderBy === 'cost') {
      const aCost = a.items.reduce((sum, item) => sum + (item.quantity * (item.costPrice || 0)), 0);
      const bCost = b.items.reduce((sum, item) => sum + (item.quantity * (item.costPrice || 0)), 0);
      return order === 'asc' ? aCost - bCost : bCost - aCost;
    }
    
    if (orderBy === 'profit') {
      const aProfit = a.items.reduce((sum, item) => sum + (item.quantity * ((item.soldPrice || 0) - (item.costPrice || 0))), 0);
      const bProfit = b.items.reduce((sum, item) => sum + (item.quantity * ((item.soldPrice || 0) - (item.costPrice || 0))), 0);
      return order === 'asc' ? aProfit - bProfit : bProfit - aProfit;
    }
    
    const aValue = a[orderBy] || 0;
    const bValue = b[orderBy] || 0;
    return order === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatFrenchDate = (dateString, formatType = 'full') => {
    const date = parseISO(dateString);
    switch(formatType) {
      case 'full':
        return format(date, 'EEEE d MMMM yyyy', { locale: fr });
      case 'short':
        return format(date, 'dd/MM/yyyy', { locale: fr });
      case 'day':
        return format(date, 'EEEE', { locale: fr });
      case 'day-short':
        return format(date, 'EEE', { locale: fr }).toUpperCase();
      case 'month-year':
        return format(date, 'MMMM yyyy', { locale: fr });
      default:
        return format(date, 'd MMMM yyyy', { locale: fr });
    }
  };

  // Handle edit for grouped servings
  const handleEditGroup = (serving) => {
    if (serving.originalServings && serving.originalServings.length > 1) {
      // For grouped servings, show a dialog or edit each individually
      alert(`${serving.originalServings.length} services trouvés pour cette date. Veuillez les modifier individuellement.`);
    } else if (serving.originalServings && serving.originalServings.length === 1) {
      editDailyServing(serving.originalServings[0]);
    } else {
      editDailyServing(serving);
    }
  };

  // Handle delete for grouped servings
  const handleDeleteGroup = async (serving) => {
    if (serving.originalServings && serving.originalServings.length > 1) {
      if (window.confirm(`Supprimer tous les ${serving.originalServings.length} services du ${formatFrenchDate(serving.date, 'short')} ?`)) {
        for (const originalServing of serving.originalServings) {
          await deleteDailyServing(originalServing.id);
        }
      }
    } else if (serving.originalServings && serving.originalServings.length === 1) {
      deleteDailyServing(serving.originalServings[0].id);
    } else {
      deleteDailyServing(serving.id);
    }
  };

  return (
    <Card sx={{ 
      borderRadius: 4,
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      overflow: 'hidden',
      bgcolor: '#FFFFFF'
    }}>
      {/* Header with Stats */}
      <Box sx={{ 
        p: 3,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <Kitchen />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Suivi des Portions Servies
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {dailyServings.length} enregistrements • {groupByDate ? `${groupedServings.length} jours` : `${dailyServings.length} services`} • {stats.totalItems} articles vendus
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={mergeSameItems ? "Désactiver le regroupement des articles identiques" : "Regrouper les articles identiques"} arrow>
              <IconButton 
                onClick={() => setMergeSameItems(!mergeSameItems)}
                sx={{ 
                  color: 'white', 
                  bgcolor: mergeSameItems ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } 
                }}
              >
                <MergeType />
              </IconButton>
            </Tooltip>
            <Tooltip title={groupByDate ? "Désactiver le regroupement par date" : "Grouper par date"} arrow>
              <IconButton 
                onClick={() => setGroupByDate(!groupByDate)}
                sx={{ 
                  color: 'white', 
                  bgcolor: groupByDate ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } 
                }}
              >
                <CalendarToday />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rafraîchir" arrow>
              <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Télécharger" arrow>
              <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimer" arrow>
              <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <Print />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}>
                  <AttachMoney />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                    Chiffre d'affaires
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                    {formatCurrency(stats.totalRevenue)}
                  </Typography>
                </Box>
              </Box>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#FF9800', 0.1), color: '#FF9800' }}>
                  <Receipt />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                    Coût total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                    {formatCurrency(stats.totalCost)}
                  </Typography>
                </Box>
              </Box>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                    Bénéfice total
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    color: stats.totalProfit >= 0 ? '#4CAF50' : '#f44336'
                  }}>
                    {formatCurrency(stats.totalProfit)}
                  </Typography>
                </Box>
              </Box>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#9C27B0', 0.1), color: '#9C27B0' }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                    Marge moyenne
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF9800' }}>
                    {avgMargin.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </MetricCard>
          </Grid>
        </Grid>
      </Box>

      {/* Toolbar */}
      <Box sx={{ 
        p: 2,
        bgcolor: '#F8F9FA',
        borderBottom: '1px solid #E0E0E0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<CalendarToday />}
            label={formatFrenchDate(`${selectedMonth}-01`, 'month-year')}
            sx={{ 
              bgcolor: '#667eea',
              color: 'white',
              '& .MuiChip-icon': { color: 'white' },
              textTransform: 'capitalize'
            }}
          />
          
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            <DatePicker
              label="Filtrer par date"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { 
                    width: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white'
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>

          {selectedDate && (
            <Chip
              label={formatFrenchDate(selectedDate, 'short')}
              onDelete={() => setSelectedDate(null)}
              size="small"
              sx={{ bgcolor: alpha('#f44336', 0.1), color: '#f44336' }}
            />
          )}
          
          {groupByDate && (
            <Chip
              icon={<CalendarToday />}
              label="Groupé par date"
              size="small"
              sx={{ bgcolor: alpha('#667eea', 0.1), color: '#667eea' }}
            />
          )}
          
          {mergeSameItems && (
            <Chip
              icon={<MergeType />}
              label="Articles fusionnés"
              size="small"
              sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={() => handleMonthChange('prev')}
            sx={{ 
              border: '1px solid #E0E0E0',
              borderRadius: 2,
              '&:hover': { bgcolor: alpha('#667eea', 0.1) }
            }}
          >
            <ArrowLeft />
          </IconButton>
          <IconButton 
            onClick={() => handleMonthChange('next')}
            sx={{ 
              border: '1px solid #E0E0E0',
              borderRadius: 2,
              '&:hover': { bgcolor: alpha('#667eea', 0.1) }
            }}
          >
            <ArrowRight />
          </IconButton>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ 
        maxHeight: 'calc(100vh - 300px)',
        '&::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          background: '#F5F7FA',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#B0BEC5',
          borderRadius: 4,
          '&:hover': {
            background: '#90A4AE',
          }
        }
      }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50, bgcolor: '#F5F7FA' }} />
              <TableCell sx={{ bgcolor: '#F5F7FA' }}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleSortRequest('date')}
                  sx={{ fontWeight: 700 }}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#F5F7FA' }}>
                <TableSortLabel
                  active={orderBy === 'revenue'}
                  direction={orderBy === 'revenue' ? order : 'asc'}
                  onClick={() => handleSortRequest('revenue')}
                  sx={{ fontWeight: 700 }}
                >
                  Chiffre d'affaires
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#F5F7FA' }}>
                <TableSortLabel
                  active={orderBy === 'cost'}
                  direction={orderBy === 'cost' ? order : 'asc'}
                  onClick={() => handleSortRequest('cost')}
                  sx={{ fontWeight: 700 }}
                >
                  Coût
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#F5F7FA' }}>
                <TableSortLabel
                  active={orderBy === 'profit'}
                  direction={orderBy === 'profit' ? order : 'asc'}
                  onClick={() => handleSortRequest('profit')}
                  sx={{ fontWeight: 700 }}
                >
                  Bénéfice
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#F5F7FA' }}>
                Marge
              </TableCell>
              <TableCell align="center" sx={{ bgcolor: '#F5F7FA', width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <AnimatePresence>
              {sortedServings.map((serving, index) => {
                const totalCost = serving.items.reduce((sum, item) => 
                  sum + (item.quantity * (item.costPrice || 0)), 0);
                const totalRevenue = serving.items.reduce((sum, item) => 
                  sum + (item.quantity * (item.soldPrice || 0)), 0);
                const profit = totalRevenue - totalCost;
                const margin = totalRevenue > 0 ? (profit / totalRevenue * 100) : 0;
                const isGrouped = serving.originalServings && serving.originalServings.length > 1;

                return (
                  <Fragment key={serving.id}>
                    <StyledTableRow 
                      hover
                      onClick={() => setExpandedRow(expandedRow === serving.id ? null : serving.id)}
                      selected={expandedRow === serving.id}
                      onMouseEnter={() => setHoveredRow(serving.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      sx={{
                        transform: hoveredRow === serving.id ? 'scale(1.001)' : 'scale(1)',
                        transition: 'all 0.2s ease',
                        bgcolor: isGrouped ? alpha('#667eea', 0.02) : 'transparent'
                      }}
                    >
                      <TableCell>
                        <Tooltip title={expandedRow === serving.id ? "Réduire" : "Détails"} arrow>
                          <IconButton size="small" sx={{ 
                            bgcolor: expandedRow === serving.id ? alpha('#667eea', 0.1) : 'transparent',
                            '&:hover': { bgcolor: alpha('#667eea', 0.1) }
                          }}>
                            {expandedRow === serving.id ? 
                              <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Badge
                            badgeContent={isGrouped ? serving.originalServings?.length : serving.items.length}
                            color={isGrouped ? "secondary" : "primary"}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.6rem',
                                height: 16,
                                minWidth: 16
                              }
                            }}
                          >
                            <Avatar sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: alpha(isGrouped ? '#FF9800' : '#667eea', 0.1),
                              color: isGrouped ? '#FF9800' : '#667eea'
                            }}>
                              {isGrouped ? <MergeType fontSize="small" /> : <Event fontSize="small" />}
                            </Avatar>
                          </Badge>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {formatFrenchDate(serving.date, 'full')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                              {isGrouped 
                                ? `${serving.originalServings?.length || 0} services • ${serving.items.length} articles distincts • ${serving.items.reduce((sum, i) => sum + i.quantity, 0)} portions`
                                : `${serving.items.length} articles distincts • ${serving.items.reduce((sum, i) => sum + i.quantity, 0)} portions`
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                          {formatCurrency(totalRevenue)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                          {formatCurrency(totalCost)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                          {profit >= 0 ? 
                            <TrendingUp sx={{ fontSize: 18, color: '#4CAF50' }} /> : 
                            <TrendingDown sx={{ fontSize: 18, color: '#f44336' }} />}
                          <Typography variant="body2" sx={{ 
                            fontWeight: 700,
                            color: profit >= 0 ? '#4CAF50' : '#f44336'
                          }}>
                            {formatCurrency(Math.abs(profit))}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(margin, 100)}
                            sx={{ 
                              width: 60,
                              height: 4,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.divider, 0.2),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: margin >= 30 ? '#4CAF50' : margin >= 20 ? '#FF9800' : '#f44336'
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600,
                            color: margin >= 30 ? '#4CAF50' : margin >= 20 ? '#FF9800' : '#f44336'
                          }}>
                            {margin.toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.5,
                          justifyContent: 'center'
                        }}>
                          <Tooltip title="Modifier" arrow>
                            <IconButton 
                              onClick={(e) => { e.stopPropagation(); handleEditGroup(serving); }}
                              size="small"
                              sx={{ 
                                color: '#667eea',
                                '&:hover': { bgcolor: alpha('#667eea', 0.1) }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer" arrow>
                            <IconButton 
                              onClick={(e) => { e.stopPropagation(); handleDeleteGroup(serving); }}
                              size="small"
                              sx={{ 
                                color: '#f44336',
                                '&:hover': { bgcolor: alpha('#f44336', 0.1) }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </StyledTableRow>

                    {/* Expanded Details */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                        <Collapse in={expandedRow === serving.id} timeout="auto" unmountOnExit>
                          <Box sx={{ 
                            p: 3,
                            bgcolor: '#F8F9FA',
                            borderTop: '1px solid #E0E0E0',
                            borderBottom: '1px solid #E0E0E0'
                          }}>
                            {isGrouped && serving.originalServings && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FF9800', mb: 1 }}>
                                  {serving.originalServings.length} services combinés pour cette date
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                              </Box>
                            )}
                            
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1A2B3C' }}>
                              Détail des articles vendus le {formatFrenchDate(serving.date, 'full')}
                              {mergeSameItems && (
                                <Chip 
                                  label="Articles identiques fusionnés" 
                                  size="small" 
                                  icon={<MergeType />}
                                  sx={{ ml: 2, bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}
                                />
                              )}
                            </Typography>
                            
                            <Table size="small" sx={{ 
                              bgcolor: 'white',
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '1px solid #E0E0E0'
                            }}>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#F5F7FA' }}>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Produit</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Quantité</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Prix unitaire</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Chiffre d'affaires</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Coût</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Marge</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {serving.items.map((item, idx) => {
                                  const revenue = item.quantity * item.soldPrice;
                                  const cost = item.quantity * item.costPrice;
                                  const itemMargin = revenue > 0 ? ((revenue - cost) / revenue * 100) : 0;

                                  return (
                                    <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Avatar sx={{ width: 24, height: 24, bgcolor: alpha('#667eea', 0.1) }}>
                                            <Restaurant sx={{ fontSize: 14, color: '#667eea' }} />
                                          </Avatar>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {item.productName}
                                          </Typography>
                                          {item.quantity > 1 && (
                                            <Chip 
                                              label={`×${item.quantity}`} 
                                              size="small" 
                                              sx={{ 
                                                height: 20, 
                                                fontSize: '0.7rem',
                                                bgcolor: alpha('#667eea', 0.1),
                                                color: '#667eea'
                                              }} 
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Chip
                                          label={item.quantity}
                                          size="small"
                                          sx={{ 
                                            bgcolor: alpha('#667eea', 0.1),
                                            color: '#667eea',
                                            fontWeight: 600,
                                            minWidth: 40
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="right">{item.soldPrice.toFixed(2)} DT</TableCell>
                                      <TableCell align="right" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                                        {revenue.toFixed(2)} DT
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#5F6B7A' }}>
                                        {cost.toFixed(2)} DT
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                          <LinearProgress 
                                            variant="determinate" 
                                            value={Math.min(itemMargin, 100)}
                                            sx={{ 
                                              width: 50,
                                              height: 4,
                                              borderRadius: 2,
                                              bgcolor: alpha(theme.palette.divider, 0.2),
                                              '& .MuiLinearProgress-bar': {
                                                bgcolor: itemMargin >= 30 ? '#4CAF50' : itemMargin >= 20 ? '#FF9800' : '#f44336'
                                              }
                                            }}
                                          />
                                          <Typography variant="caption" sx={{ 
                                            fontWeight: 600,
                                            color: itemMargin >= 30 ? '#4CAF50' : itemMargin >= 20 ? '#FF9800' : '#f44336'
                                          }}>
                                            {itemMargin.toFixed(1)}%
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </AnimatePresence>

            {sortedServings.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Fade in={true}>
                    <Box sx={{ 
                      py: 8, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 2,
                      color: 'text.secondary'
                    }}>
                      <Kitchen sx={{ fontSize: 80, color: '#B0BEC5', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#5F6B7A' }}>
                        Aucun enregistrement trouvé
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#9AA6B2' }}>
                        {selectedDate || selectedMonth !== format(new Date(), 'yyyy-MM') 
                          ? 'Essayez d\'ajuster vos filtres' 
                          : 'Commencez par créer un nouveau plan de service'}
                      </Typography>
                    </Box>
                  </Fade>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      {sortedServings.length > 0 && (
        <Box sx={{ 
          p: 2,
          bgcolor: '#F8F9FA',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
            {sortedServings.length} {groupByDate ? 'jour(s)' : 'enregistrement(s)'} • 
            {stats.totalItems} articles vendus • 
            Dernière mise à jour: {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip
              size="small"
              icon={<CheckCircle />}
              label={`${sortedServings.filter(s => {
                const rev = s.items.reduce((sum, i) => sum + i.quantity * i.soldPrice, 0);
                const cost = s.items.reduce((sum, i) => sum + i.quantity * i.costPrice, 0);
                return (rev - cost) > 0;
              }).length} rentables`}
              sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}
            />
            <Chip
              size="small"
              icon={<Warning />}
              label={`${sortedServings.filter(s => {
                const rev = s.items.reduce((sum, i) => sum + i.quantity * i.soldPrice, 0);
                const cost = s.items.reduce((sum, i) => sum + i.quantity * i.costPrice, 0);
                return (rev - cost) <= 0;
              }).length} non rentables`}
              sx={{ bgcolor: alpha('#f44336', 0.1), color: '#f44336' }}
            />
          </Box>
        </Box>
      )}
    </Card>
  );
};